import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useVisitorActions } from "../../hooks/useVisitorRequests";

export default function KioskFlowPage() {
  const { communityId } = useParams();
  const { createRequest } = useVisitorActions();
  const [community, setCommunity] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [blockName, setBlockName] = useState("");
  const [floor, setFloor] = useState(null);
  const [flat, setFlat] = useState("");

  const [visitorType, setVisitorType] = useState("delivery");
  const [visitorName, setVisitorName] = useState("");
  const [phone, setPhone] = useState("");
  const [purpose, setPurpose] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "communities", communityId));
        if (!snap.exists()) {
          setErr("Community not found");
          return;
        }
        setCommunity({ id: snap.id, ...snap.data() });
      } catch (e) {
        setErr(e?.message || "Failed to load community");
      }
    })();
  }, [communityId]);

  const blocks = Array.isArray(community?.blocks) ? community.blocks : [];
  const currentBlock = useMemo(
    () => blocks.find((b) => b.name === blockName),
    [blocks, blockName],
  );

  const flatsOnFloor = useMemo(() => {
    if (!currentBlock || !floor) return [];
    const list = Array.from({ length: currentBlock.flatsPerFloor || 0 }).map(
      (_, i) => {
        const idx = (currentBlock.upperIndexStart ?? 1) + i;
        const width = currentBlock.upperIndexWidth ?? 2;
        return `${currentBlock.name}-${floor}${String(idx).padStart(
          width,
          "0",
        )}`;
      },
    );
    return list;
  }, [currentBlock, floor]);

  const submit = async () => {
    setErr("");
    setOk("");
    if (!blockName || !floor || !flat || !visitorName) {
      setErr("Please complete all selections and visitor name");
      return;
    }
    try {
      await createRequest({
        communityId,
        blockName,
        floor: Number(floor),
        flatLabel: flat,
        visitorType,
        visitorName: visitorName.trim(),
        phone: phone.trim(),
        purpose: purpose.trim(),
        createdBy: { role: "visitor", method: "community-qr" },
        status: "pending",
        approval: { requestedAt: serverTimestamp() },
      });
      setOk("Request submitted. Please wait for approval at the gate.");
      setVisitorName("");
      setPhone("");
      setPurpose("");
    } catch (e) {
      setErr(e?.message || "Failed to submit");
    }
  };

  return (
    <div className="container py-3">
      <h5 className="mb-3">Welcome</h5>
      {err && <Alert variant="danger">{err}</Alert>}
      {ok && <Alert variant="success">{ok}</Alert>}

      {/* Blocks */}
      <div className="mb-3">
        <div className="fw-semibold mb-2">Select Block</div>
        <div className="d-flex flex-wrap gap-2">
          {blocks.map((b) => (
            <Button
              key={b.name}
              variant={b.name === blockName ? "primary" : "outline-primary"}
              onClick={() => {
                setBlockName(b.name);
                setFloor(null);
                setFlat("");
              }}
            >
              {b.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Floors */}
      {currentBlock && (
        <div className="mb-3">
          <div className="fw-semibold mb-2">Select Floor</div>
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: currentBlock.floors || 0 }).map((_, i) => {
              const f = i + 1;
              return (
                <Button
                  key={f}
                  variant={f === floor ? "primary" : "outline-primary"}
                  onClick={() => {
                    setFloor(f);
                    setFlat("");
                  }}
                >
                  {f}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Flats */}
      {floor && (
        <div className="mb-3">
          <div className="fw-semibold mb-2">Select Flat</div>
          <div className="d-flex flex-wrap gap-2">
            {flatsOnFloor.map((fl) => (
              <Button
                key={fl}
                variant={fl === flat ? "primary" : "outline-primary"}
                onClick={() => setFlat(fl)}
              >
                {fl}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Visitor details */}
      <div className="mb-2">
        <div className="fw-semibold mb-2">Visitor Details</div>
        <div className="row g-2">
          <div className="col-md-3">
            <select
              className="form-select"
              value={visitorType}
              onChange={(e) => setVisitorType(e.target.value)}
            >
              <option value="delivery">Delivery</option>
              <option value="guest">Guest</option>
              <option value="service">Service</option>
              <option value="cab">Cab</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-md-3">
            <input
              className="form-control"
              placeholder="Visitor Name"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <input
              className="form-control"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <input
              className="form-control"
              placeholder="Purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </div>
          <div className="col-12 mt-2">
            <Button onClick={submit} disabled={!flat || !visitorName}>
              Submit for Approval
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
